import React from 'react';
import { useTranslations } from '@hooks/useTranslations';
import Icon, { Icons } from '@components/Icon';
import { FAQ_URL_STRING, isBrandAC, MARKET } from 'src/constants/main';
import { buildParameterizedPathname } from '@utils/url.utils';
import routes from 'src/constants/routes';
import { useSelector } from 'react-redux';
import { IGlobalState } from 'src/redux';
import { CarJourneyType } from 'src/services/carList/carList.types';
import GlobalDuck from 'src/redux/global/global.duck';
import { GtmData } from 'src/types/gtm';
import { useGTM } from '@hooks/useGTM';

export interface INeedHelpSectionProps {
    phoneNumber: string;
}

interface IProps extends INeedHelpSectionProps {
    className?: string;
}

const PAGES_WITH_FAQ = [
    routes.SELECTOR,
    routes.CAR,
    routes.BASKET,
    routes.SUMMARY,
    routes.OFFERS_DELIVERY,
    routes.OFFERS_LOGIN,
];

const NeedHelpSection = ({ className, phoneNumber }: IProps) => {
    const { t } = useTranslations();
    const carJourney = useSelector<IGlobalState, CarJourneyType>((state) => GlobalDuck.getCarJourney(state));
    const { pushToDataLayer } = useGTM();
    const getFAQsURL = () => {
        const hashURLPart = `#${FAQ_URL_STRING}`;

        if (
            !PAGES_WITH_FAQ.some((allowedRoute) => (window?.location?.pathname ?? '').includes(allowedRoute)) &&
            !(
                window?.location?.pathname.includes(CarJourneyType.CONFIGURABLE) ||
                window?.location?.pathname.includes(CarJourneyType.STOCK)
            )
        ) {
            return `${buildParameterizedPathname(
                routes.ROOT,
                carJourney ?? CarJourneyType.CONFIGURABLE
            )}${hashURLPart}`;
        }

        const currentURL = window?.location?.href ?? '';
        const hashCharacterIndex = currentURL.indexOf('#');
        const URLWithoutHash = hashCharacterIndex === -1 ? currentURL : currentURL.substring(0, hashCharacterIndex);

        return `${URLWithoutHash}${hashURLPart}`;
    };

    return (
        <div className={className}>
            <div className="need-help-section__contact">
                <p className="need-help-section__title">{t('header.nav.needHelp')}</p>
                <p className="need-help-section__title">{t('header.nav.callUs')}</p>
                <a
                    onClick={() =>
                        pushToDataLayer({
                            event: 'uaevent',
                            eventCategory: 'Header',
                            eventAction: `Call::SOLRetail::${MARKET}`,
                            eventLabel: t('header.nav.needHelp'),
                        } as GtmData)
                    }
                    className="need-help-section__link need-help-section__phone"
                    href={`tel:${phoneNumber.trim()}`}
                >
                    {phoneNumber}
                </a>
                <p className="need-help-section__text need-help-section__call-text">{t('header.nav.callUsTime')}</p>
                <p className="need-help-section__text need-help-section__call-text">
                    {t('header.nav.callFreeOfCharge')}
                </p>
            </div>

            <div className="need-help-section__faq">
                <p className="need-help-section__title">{t('header.nav.anyQuestions')}</p>
                <p className="need-help-section__title">{t('header.nav.checkFAQ')}</p>
                <a
                    href={getFAQsURL()}
                    className="need-help-section__link need-help-section__faq"
                    onClick={() =>
                        pushToDataLayer({
                            event: 'uaevent',
                            eventCategory: 'Header',
                            eventAction: `Redirection::Internal`,
                            eventLabel: 'Most common questions',
                        } as GtmData)
                    }
                >
                    <span>{t('header.nav.FAQLink')}</span>
                    {!isBrandAC && (
                        <Icon height={10} name={Icons.ChevronRight} className="need-help-section__link__icon" />
                    )}
                </a>
            </div>
            <span className="need-help-section__closeIcon">
                <Icon height={10} name={Icons.Cancel} className="need-help-section__chevron" />
            </span>
        </div>
    );
};

export default NeedHelpSection;
